<template>
  <div class="chart">
    <div class="chart__controls">
      <a class="chart__pagination-prev" @click="paginationPrev()">
        <IconCaret />
      </a>
      <div class="chart__granularity">
        <Datepicker
          :value="chartDate"
          :language="datePicker.translations"
          :disabled-dates="datePicker.disabledDates"
          :format="datePicker.format"
          :required="true"
          :monday-first="true"
          maximum-view="day"
          input-class="vdp-datepicker__calendar-input"
          @selected="onCalendarSelected"
        ></Datepicker>
      </div>
      <a :class="paginationNextClasses" @click="paginationNext()">
        <IconCaret />
      </a>
    </div>
    <div class="chart__container">
      <canvas class="chart__canvas" ref="chart"></canvas>
    </div>
    <PowerStatistics />
    <div :class="overlayClasses">
      <IconSpinner :extra-classes="['chart__spinner']" />
    </div>
  </div>
</template>

<script>
  import Chart from 'chart.js'
  import Datepicker from 'vuejs-datepicker'
  import IconCaret from '@/components/icons/Caret'
  import IconSpinner from '@/components/icons/Spinner'
  import PowerStatistics from '@/components/history/PowerStatistics'

  import moment from 'moment'
  import { de } from 'vuejs-datepicker/dist/locale'

  export default {
    name: 'chart',
    components: {
      Datepicker,
      IconCaret,
      IconSpinner,
      PowerStatistics,
    },
    data() {
      let result = {
        loading: false,
        chart: false,
        granularity: '15min',
        datePicker: {
          translations: de,
          disabledDates: {
            from: moment().endOf('day').toDate(),
          },
          format: 'dd.MM.yyyy',
        },
        config: {
          type: 'line',
          options: {
            maintainAspectRatio: false,
            fontColor: '#dddddd',
            legend: {
              position: 'bottom',
            },
            scales: {
              xAxes: [
                {
                  barPercentage: 0.8,
                  categoryPercentage: 1,
                  categorySpacing: 1,
                  stacked: true,
                  gridLines: {
                    color: 'rgba(0, 0, 0, 0.03)',
                  },
                  display: true,
                  ticks: {
                    callback: (dataLabel, index) => {
                      if (this.granularity === '1min') {
                        return index % 5 === 0 ? dataLabel : ''
                      } else {
                        return index % 4 === 0 ? dataLabel : ''
                      }
                    },
                  },
                },
              ],
              yAxes: [
                {
                  id: 'yAbove',
                  stacked: true,
                  ticks: {
                    callback: (value) => {
                      return this.formatWattage(Math.abs(value))
                    },
                  },
                  gridLines: {
                    color: 'rgba(0, 0, 0, 0.03)',
                  },
                },
                {
                  id: 'yNonStacked',
                  stacked: false,
                  display: false,
                  ticks: {
                    callback: (value) => {
                      return this.formatWattage(Math.abs(value))
                    },
                  },
                  gridLines: {
                    color: 'rgba(0, 0, 0, 0.03)',
                  },
                },
              ],
            },
            tooltips: {
              mode: 'index',
              callbacks: {
                title: (item, data) => {
                  return data.labels[item[0].index] + 'h'
                },
                label: (item, data) => {
                  const dataset = data.datasets[item.datasetIndex]
                  const value = (item.yLabel + '').replace(/^-/, '')
                  let label = dataset.label
                  if (item.yLabel < 0 && dataset.negativeLabel) {
                    label = dataset.negativeLabel
                  } else if (item.yLabel > 0 && dataset.positiveLabel) {
                    label = dataset.positiveLabel
                  }
                  return label + ': ' + this.formatWattage(value)
                },
              },
            },
          },
          data: {
            labels: [],
            datasets: [
              {
                label: 'Vom/an den Marktplatz',
                negativeLabel: 'An den Markplatz geliefert',
                positiveLabel: 'Vom Markplatz bezogen',
                fill: 'origin',
                lineTension: 0.8,
                cubicInterpolationMode: 'monotone',
                backgroundColor: process.env.VUE_APP_SCSS_COLOR_COMMUNITY,
                data: [],
                borderJoinStyle: 'round',
                borderColor: 'transparent',
                pointStyle: 'line',
                yAxisID: 'yAbove',
              },
              {
                label: 'Von/an ' + process.env.VUE_APP_SUPPLIER_NAME,
                negativeLabel:
                  'An ' + process.env.VUE_APP_SUPPLIER_NAME + ' geliefert',
                positiveLabel:
                  'Von ' + process.env.VUE_APP_SUPPLIER_NAME + ' bezogen',
                fill: 'origin',
                backgroundColor: 'rgba(180, 220, 255, 1)',
                data: [],
                lineTension: 0.8,
                cubicInterpolationMode: 'monotone',
                borderJoinStyle: 'round',
                borderColor: 'transparent',
                pointStyle: 'line',
                yAxisID: 'yAbove',
              },
              {
                label: 'Überschuss',
                fill: 'origin',
                lineTension: 0.8,
                cubicInterpolationMode: 'monotone',
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                data: [],
                borderJoinStyle: 'round',
                borderColor: 'transparent',
                pointStyle: 'line',
                yAxisID: 'yAbove',
              },
            ],
          },
        },
        dataIndexes: {
          community: 0,
          supplier: 1,
          consumable: 2,
        },
      }

      const inverterDatasetConfig = {
        label: 'Wechselrichter Produktionsleistung',
        fill: false,
        borderWidth: 2,
        lineTension: 0.8,
        cubicInterpolationMode: 'monotone',
        pointRadius: 0,
        data: [],
        borderJoinStyle: 'round',
        borderColor: 'rgba(109, 130, 96, 0.8)',
        pointStyle: 'none',
        yAxisID: 'yNonStacked',
      }

      if (this.$store.getters.showInverterData) {
        result.config.data.datasets.unshift(inverterDatasetConfig)
        result.dataIndexes = {
          inverter: 0,
          community: 1,
          supplier: 2,
          consumable: 3,
        }
      }

      return result
    },
    mounted() {
      this.updateChart(this.$store.getters.chartData)
      window.addEventListener('resize', this.onResize)
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize)
    },
    computed: {
      chartDate() {
        return this.$store.getters.historySelectedDate
      },
      chartData() {
        return this.$store.getters.chartData
      },
      paginationNextClasses() {
        let classes = ['chart__pagination-next']

        const midnight = moment().startOf('day')
        if (moment(this.chartDate).isSameOrAfter(midnight)) {
          classes.push('chart__pagination-next--disabled')
        }
        return classes.join(' ')
      },
      overlayClasses() {
        let result = ['chart__overlay']
        if (this.loading) {
          result.push('chart__overlay--visible')
        }
        return result.join(' ')
      },
    },
    methods: {
      updateChart: function (data) {
        // Dont show/update the chart without any data.
        if (data.labels.length < 1) {
          return false
        }

        const communityIndex = this.dataIndexes.community
        const supplierIndex = this.dataIndexes.supplier
        const consumableIndex = this.dataIndexes.consumable
        let inverterIndex = null
        if (this.$store.getters.showInverterData) {
          inverterIndex = this.dataIndexes.inverter
        }

        if (!this.chart) {
          let chartSettings = {
            ...this.config,
          }
          chartSettings.options.legend.display = this.showLegend()

          chartSettings.data.labels = data.labels
          chartSettings.data.datasets[communityIndex].data =
            data.datasets.community
          chartSettings.data.datasets[supplierIndex].data =
            data.datasets.supplier
          chartSettings.data.datasets[consumableIndex].data =
            data.datasets.communityConsumable

          if (this.$store.getters.showInverterData) {
            chartSettings.data.datasets[inverterIndex].data =
              data.datasets.inverterProduction

            // Adapt the scales to match.
            let maxYTick = Math.max(
              this.getMaxNumberFromArray(
                chartSettings.data.datasets[inverterIndex].data
              ),
              this.getMaxNumberFromArray(
                chartSettings.data.datasets[consumableIndex].data
              ),
              this.getMaxNumberFromArray(
                chartSettings.data.datasets[supplierIndex].data
              ),
              this.getMaxNumberFromArray(
                chartSettings.data.datasets[communityIndex].data
              )
            )

            let minYTick = Math.min(
              this.getMinNumberFromArray(
                chartSettings.data.datasets[inverterIndex].data
              ),
              this.getMinNumberFromArray(
                chartSettings.data.datasets[consumableIndex].data
              ),
              this.getMinNumberFromArray(
                chartSettings.data.datasets[supplierIndex].data
              ),
              this.getMinNumberFromArray(
                chartSettings.data.datasets[communityIndex].data
              )
            )

            // create new array with sums
            let prodArray = []
            for (
              let i = 0;
              i < chartSettings.data.datasets[supplierIndex].data.length;
              i++
            ) {
              if (chartSettings.data.datasets[communityIndex].data.length > i) {
                if (
                  chartSettings.data.datasets[communityIndex].data[i] < 0 &&
                  chartSettings.data.datasets[supplierIndex].data[i] < 0
                ) {
                  prodArray.push(
                    -(
                      Math.abs(
                        chartSettings.data.datasets[communityIndex].data[i]
                      ) +
                      Math.abs(
                        chartSettings.data.datasets[supplierIndex].data[i]
                      )
                    )
                  )
                }
              }
            }

            let minSumValue = this.getMinNumberFromArray(prodArray)
            minYTick = Math.min(minSumValue, minYTick)

            let consArray = []
            for (
              let i = 0;
              i < chartSettings.data.datasets[supplierIndex].data.length;
              i++
            ) {
              if (
                chartSettings.data.datasets[communityIndex].data[i] > 0 &&
                chartSettings.data.datasets[supplierIndex].data[i] > 0
              ) {
                consArray.push(
                  chartSettings.data.datasets[communityIndex].data[i] +
                    chartSettings.data.datasets[supplierIndex].data[i]
                )
              }
            }

            let maxSumValue = this.getMaxNumberFromArray(consArray)
            maxYTick = Math.max(maxSumValue, maxYTick)

            maxYTick = Math.ceil(maxYTick / 500) * 500
            minYTick = Math.floor(minYTick / 500) * 500

            chartSettings.options.scales.yAxes[0].ticks.max = maxYTick
            chartSettings.options.scales.yAxes[1].ticks.max = maxYTick
            chartSettings.options.scales.yAxes[0].ticks.min = minYTick
            chartSettings.options.scales.yAxes[1].ticks.min = minYTick

            chartSettings.options.scales.yAxes[0].ticks.suggestedMax = maxYTick
            chartSettings.options.scales.yAxes[1].ticks.suggestedMax = maxYTick
            chartSettings.options.scales.yAxes[0].ticks.suggestedMin = minYTick
            chartSettings.options.scales.yAxes[1].ticks.suggestedMin = minYTick
          }

          this.chart = new Chart(this.$refs.chart, chartSettings)
        } else {
          this.chart.labels = []
          this.chart.labels = data.labels
          this.chart.data.datasets[communityIndex].data = []
          this.chart.data.datasets[supplierIndex].data = []
          this.chart.data.datasets[consumableIndex].data = []
          this.chart.data.datasets[communityIndex].data =
            data.datasets.community
          this.chart.data.datasets[supplierIndex].data = data.datasets.supplier
          this.chart.data.datasets[consumableIndex].data =
            data.datasets.communityConsumable
          if (this.$store.getters.showInverterData) {
            this.chart.data.datasets[inverterIndex].data = []
            this.chart.data.datasets[inverterIndex].data =
              data.datasets.inverterProduction

            // Adapt the scales to match.
            let maxYTick = Math.max(
              this.getMaxNumberFromArray(
                this.chart.data.datasets[inverterIndex].data
              ),
              this.getMaxNumberFromArray(
                this.chart.data.datasets[consumableIndex].data
              ),
              this.getMaxNumberFromArray(
                this.chart.data.datasets[supplierIndex].data
              ),
              this.getMaxNumberFromArray(
                this.chart.data.datasets[communityIndex].data
              )
            )

            let minYTick = Math.min(
              this.getMinNumberFromArray(
                this.chart.data.datasets[inverterIndex].data
              ),
              this.getMinNumberFromArray(
                this.chart.data.datasets[consumableIndex].data
              ),
              this.getMinNumberFromArray(
                this.chart.data.datasets[supplierIndex].data
              ),
              this.getMinNumberFromArray(
                this.chart.data.datasets[communityIndex].data
              )
            )

            // create new array with sums
            let prodArray = []
            for (
              let i = 0;
              i < this.chart.data.datasets[supplierIndex].data.length;
              i++
            ) {
              if (this.chart.data.datasets[communityIndex].data.length > i) {
                if (
                  this.chart.data.datasets[communityIndex].data[i] < 0 &&
                  this.chart.data.datasets[supplierIndex].data[i] < 0
                ) {
                  prodArray.push(
                    -(
                      Math.abs(
                        this.chart.data.datasets[communityIndex].data[i]
                      ) +
                      Math.abs(this.chart.data.datasets[supplierIndex].data[i])
                    )
                  )
                }
              }
            }

            let minSumValue = this.getMinNumberFromArray(prodArray)
            minYTick = Math.min(minSumValue, minYTick)

            let consArray = []
            for (
              let i = 0;
              i < this.chart.data.datasets[supplierIndex].data.length;
              i++
            ) {
              if (
                this.chart.data.datasets[communityIndex].data[i] > 0 &&
                this.chart.data.datasets[supplierIndex].data[i] > 0
              ) {
                consArray.push(
                  this.chart.data.datasets[communityIndex].data[i] +
                    this.chart.data.datasets[supplierIndex].data[i]
                )
              }
            }

            let maxSumValue = this.getMaxNumberFromArray(consArray)
            maxYTick = Math.max(maxSumValue, maxYTick)

            maxYTick = Math.ceil(maxYTick / 500) * 500
            minYTick = Math.floor(minYTick / 500) * 500

            this.chart.options.scales.yAxes[0].ticks.suggestedMax = maxYTick
            this.chart.options.scales.yAxes[1].ticks.suggestedMax = maxYTick
            this.chart.options.scales.yAxes[0].ticks.suggestedMin = minYTick
            this.chart.options.scales.yAxes[1].ticks.suggestedMin = minYTick

            this.chart.options.scales.yAxes[0].ticks.max = maxYTick
            this.chart.options.scales.yAxes[1].ticks.max = maxYTick
            this.chart.options.scales.yAxes[0].ticks.min = minYTick
            this.chart.options.scales.yAxes[1].ticks.min = minYTick
          }

          this.chart.update()
        }
      },

      getMaxNumberFromArray: function (array) {
        if (array === undefined || array === null || array.length === 0) {
          array = [0, 0]
        }
        return array.reduce((a, b) => Math.max(a, b))
      },
      getMinNumberFromArray: function (array) {
        if (array === undefined || array === null || array.length === 0) {
          array = [0, 0]
        }
        return array.reduce((a, b) => Math.min(a, b))
      },

      onResize: function () {
        this.chart.options.legend.display = this.showLegend()
      },
      paginationPrev: function () {
        if (this.granularity === '15min') {
          this.$store.commit(
            'setHistorySelectedDate',
            moment(this.chartDate).startOf('day').subtract(1, 'days').toDate()
          )
        } else {
          this.$store.commit(
            'setHistorySelectedDate',
            moment(this.chartDate).startOf('hour').subtract(1, 'hours').toDate()
          )
        }
        this.loadChartData()
      },
      paginationNext: function () {
        const midnight = moment().startOf('day')
        if (moment(this.chartDate).isBefore(midnight)) {
          const unit = this.granularity === '15min' ? 'days' : 'hours'

          this.$store.commit(
            'setHistorySelectedDate',
            moment(this.chartDate).add(1, unit).toDate()
          )
          this.loadChartData()
        }
      },
      onCalendarSelected(date) {
        this.$store.commit(
          'setHistorySelectedDate',
          moment(date).startOf('day').toDate()
        )
        this.loadChartData()
      },
      showLegend: function () {
        return window.innerHeight >= 500
      },
      loadChartData: function () {
        this.loading = true
        this.$store.dispatch('loadHistoryData', {
          granularity: this.granularity,
          startDate: this.chartDate,
        })
      },
    },
    watch: {
      chartData: function (data) {
        this.loading = false
        this.updateChart(data)
      },
    },
  }
</script>

<style lang="scss">
  $controls-height: 2rem;

  .chart {
    &__container {
      height: 50vh;
      position: relative;

      @media (min-height: 320px) {
        margin-top: spacer(1);
      }

      @media (min-height: 400px) {
        height: 40vh;
        margin-top: spacer(2);
      }

      @media (min-height: 600px) {
        margin-top: spacer(3);
      }
    }

    &__canvas {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
    }

    &__controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      position: relative;
      z-index: 10;
      margin-top: spacer(0.5);
    }

    &__granularity {
      display: inline-block;
      flex-basis: 8rem;

      select {
        display: inline-block;
        vertical-align: middle;
      }
    }

    &__current {
      flex-basis: 100%;
      text-align: center;

      @media (min-height: 500px) {
        font-size: $font-size-lg;
      }
    }

    &__pagination {
      &-prev,
      &-next {
        display: inline-block;
        background: $grey-lighter;
        color: $grey-dark;
        border-radius: $border-radius;
        padding: spacer(0.1) spacer(1);
        vertical-align: middle;
        transition: $transition-base;
        cursor: pointer;
        line-height: $controls-height;

        &--disabled {
          opacity: 0.5;
          cursor: default;

          @include hover-focus-active {
            color: $grey-dark;
            background: $grey-lighter;
          }
        }

        .icon {
          vertical-align: middle;
          width: spacer(1);
          transform: translateX(0.15em) rotate(90deg);

          @media (min-height: 500px) and (min-width: $bp-xs) {
            width: spacer(2);
          }
        }
      }

      &-prev {
        .icon {
          transform: translateX(-0.15em) rotate(-90deg);
        }
      }
    }

    &__overlay {
      touch-action: none;
      position: absolute;
      z-index: 10;
      top: 0;
      left: 0;
      overflow: hidden;
      opacity: 0;
      background: rgba(255, 255, 255, 0.9);
      transition: opacity 0.5s, visibility 0s linear 0.6s;
      width: 100%;
      height: 100%;
      visibility: hidden;

      &--visible {
        opacity: 1;
        transition: opacity 0.3s;
        visibility: visible;

        .chart__spinner {
          animation: spin-centered 1.8s linear 0s infinite;
        }
      }
    }

    &__spinner {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 3rem;
      height: 3rem;
      color: $community;
    }
  }

  .chart .vdp-datepicker {
    transition: $transition-base;
    $border-radius: 2px;

    &__calendar {
      box-shadow: 1px 2px 10px rgba(0, 0, 0, 0.2);
      border: none;
      left: 50%;
      transform: translateX(-50%);

      header {
        .prev,
        .next {
          transition: $transition-base;
          border-radius: $border-radius;

          &:not(.disabled):hover {
            background: $community;
          }
        }

        .prev::after {
          border-right-color: $grey-darker;
        }

        .next::after {
          border-left-color: $grey-darker;
        }
      }

      &-input {
        text-align: center;
        padding-top: 0;
        padding-bottom: 0;
        line-height: $controls-height;
      }

      .cell {
        transition: $transition-base;
        border: none;
        border-radius: $border-radius;

        &:not(.blank):not(.disabled).day:hover,
        &.selected {
          border: none;
          background: $community;
        }
      }
    }
  }
</style>
